// =============================================================================
// LiveIndicator — dot pokazujacy status realtime subscription.
// =============================================================================
// - isLive=true  → zielony dot + pulse animation + tooltip "Live"
// - isLive=false → szary dot + tooltip "Offline"
//
// Usage in a panel header:
//   <div className="flex items-center gap-2">
//     <h2>Content Engine</h2>
//     <LiveIndicator isLive={contentHook.isLive} />
//   </div>

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface LiveIndicatorProps {
  isLive: boolean;
  className?: string;
  /** Optional label shown next to the dot (default: none). */
  label?: string;
}

export function LiveIndicator({ isLive, className, label }: LiveIndicatorProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 select-none",
              className,
            )}
            aria-label={isLive ? "Live updates connected" : "Live updates offline"}
          >
            <span className="relative flex h-2 w-2">
              {isLive && (
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              )}
              <span
                className={cn(
                  "relative inline-flex rounded-full h-2 w-2",
                  isLive ? "bg-emerald-500" : "bg-muted-foreground/40",
                )}
              />
            </span>
            {label && (
              <span
                className={cn(
                  "text-xs",
                  isLive ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
                )}
              >
                {label}
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {isLive ? "Live — auto-refresh aktywny" : "Offline — odswiez strone"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
